import React from "react";
import { format } from "date-fns";

import { CustomerInfoView } from "@tiny/api";
import {
  DataTable,
  Chip,
  Typography,
  IconButton,
  Tooltip,
} from "@tiny/components";
import ExternalLink from "@tiny/icons/generated/ExternalLink";
import InfoOutline from "@tiny/icons/generated/InfoOutline";
import { DataTableColumn } from "@tiny/components/src/DataTable/DataTable.types";
import {
  SubscriberColumnView,
  SubscribersTableProps,
} from "./SubscribersTable.types";
import { useSubscribersTable } from "./SubscribersTable.hooks";
import { subscribersColumnsToDataTableColumns } from "./SubscribersTable.helpers";
import { formatFromUtc } from "@tiny/components/src/utils/dateUtils";
import EditSubscriberDialog from "./EditSubscriberDialog";
import ColumnsSettingsDialog from "./ColumnsSettingsDialog";
import SubscriberStatus from "./SubscriberStatus";
import UnsubscribeReasonDialog from "./UnsubscribeReasonDialog";
import UtmDetailsDialog from "./UtmDetailsDialog";

function SubscribersTable(props: SubscribersTableProps) {
  const {
    allSelected,
    enableEdit,
    onToggleSelect,
    onToggleSelectAll,
    selected,
    subscribers,
    columnsSettingsVisible,
    onColumnsSettingsClose,
    onRowClick,
    customAttributeNames = [],
  } = props;

  const {
    activeCustomer,
    closeEdit,
    editOpen,
    successEdit,
    moreActions,
    visibleColumns,
    visibleCustomAttributes,
    applyColumnsSettings,
    mdUp,
    unsubscribeDialogOpen,
    unsubscribeCustomer,
    closeUnsubscribeDialog,
    successUnsubscribe,
  } = useSubscribersTable(props);

  const [utmDialogCustomer, setUtmDialogCustomer] = React.useState<
    CustomerInfoView | undefined
  >();

  const subscribersListColumns = React.useMemo(() => {
    const historyColumn: DataTableColumn<CustomerInfoView> = {
      header: "View history",
      useBodyWrapper: false,
      renderBody: (item: CustomerInfoView) => (
        <Tooltip title="View history" arrow>
          <IconButton size="small" onClick={() => onRowClick(item)}>
            <ExternalLink style={{ fontSize: 16 }} color="primary" />
          </IconButton>
        </Tooltip>
      ),
      cellProps: { style: { width: 40, padding: "0 4px" } },
    };

    const columns: SubscriberColumnView[] = [
      {
        id: "email",
        useBodyWrapper: false,
        renderBody: (item: CustomerInfoView) => (
          <div onClick={() => onRowClick(item)} style={{ cursor: "pointer" }}>
            <Typography variant="body3" color="primary" tone="main" noWrap>
              {item.email}
            </Typography>
          </div>
        ),
        cellProps: {
          borderRight: true,
        },
      },
      {
        id: "status",
        useBodyWrapper: false,
        renderBody: ({ status }: CustomerInfoView) => (
          <SubscriberStatus status={status} />
        ),
      },
      {
        id: "updatedAt",
        renderBody: (item: CustomerInfoView) =>
          item?.updatedAt
            ? format(new Date(item?.updatedAt), "d MMM, yyyy")
            : "",
      },
      {
        id: "birthday",
        renderBody: (item: CustomerInfoView) =>
          item.birthday
            ? formatFromUtc(new Date(item.birthday), "MM/dd/yyyy")
            : undefined,
      },
      {
        id: "tags",
        renderBody: (item: CustomerInfoView) => (
          <>
            {item.tags?.map((tag) => (
              <Chip key={tag} label={tag} size="small" color="grey" />
            ))}
          </>
        ),
      },
      {
        id: "lastOrderDate",
        renderBody: (item: CustomerInfoView) =>
          item.lastOrderDate
            ? formatFromUtc(new Date(item.lastOrderDate), "d MMM, yyyy")
            : undefined,
      },
      {
        id: "utm",
        useBodyWrapper: false,
        renderBody: (item: CustomerInfoView) =>
          item.utm && Object.keys(item.utm).length > 0 ? (
            <div
              onClick={() => setUtmDialogCustomer(item)}
              style={{
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <InfoOutline style={{ fontSize: 14 }} color="primary" />
              <Typography variant="body3" color="primary" tone="main" noWrap>
                View
              </Typography>
            </div>
          ) : (
            "-"
          ),
      },
    ];
    const baseColumns = subscribersColumnsToDataTableColumns(
      columns,
      visibleColumns
    );

    const customAttrColumns: DataTableColumn<CustomerInfoView>[] =
      customAttributeNames
        .filter((attrName) => visibleCustomAttributes.includes(attrName))
        .map((attrName) => ({
          header: attrName,
          renderBody: (item: CustomerInfoView) => {
            const value = item.customAttributes?.[attrName];
            if (Array.isArray(value)) {
              return (
                <>
                  {value.map((v) => (
                    <Chip key={v} label={v} size="small" color="grey" />
                  ))}
                </>
              );
            }
            return value ?? "";
          },
        }));

    const [emailCol, ...restCols] = baseColumns;
    return [emailCol, historyColumn, ...restCols, ...customAttrColumns];
  }, [visibleColumns, customAttributeNames, visibleCustomAttributes]);

  const renderWrapper = React.useCallback((content: React.ReactNode) => {
    return (
      <Typography variant="body3" color="grey" tone={700} noWrap>
        {content}
      </Typography>
    );
  }, []);

  return (
    <>
      {subscribers.length > 0 && (
        <DataTable
          idKey="id"
          columns={subscribersListColumns}
          data={subscribers}
          moreActions={moreActions}
          selected={selected}
          enableEdit={enableEdit}
          renderBodyWrapper={renderWrapper}
          renderHeadWrapper={renderWrapper}
          onToggleSelect={onToggleSelect}
          allSelected={allSelected}
          onToggleSelectAll={onToggleSelectAll}
          size="small"
          allowPan
          sidePadding={mdUp ? 6 : 4}
          headerBg="#FBFCFD"
          showBorderTop
        />
      )}
      <EditSubscriberDialog
        open={editOpen}
        onClose={closeEdit}
        customer={activeCustomer}
        onSuccess={successEdit}
      />
      <UnsubscribeReasonDialog
        open={unsubscribeDialogOpen}
        onClose={closeUnsubscribeDialog}
        customer={unsubscribeCustomer}
        onSuccess={successUnsubscribe}
      />
      <ColumnsSettingsDialog
        open={columnsSettingsVisible}
        onClose={onColumnsSettingsClose}
        onApply={applyColumnsSettings}
        visibleColumns={visibleColumns}
        customAttributeNames={customAttributeNames}
        visibleCustomAttributes={visibleCustomAttributes}
      />
      <UtmDetailsDialog
        open={!!utmDialogCustomer}
        onClose={() => setUtmDialogCustomer(undefined)}
        customer={utmDialogCustomer}
      />
    </>
  );
}

export default SubscribersTable;
